import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { getContentHeight, showType } from "../../../../utils/textUtils";
import { typeInfo } from "../../../../constants/typeInfo";
import useIsMobile from "../../../../hooks/useIsMobile";
import { isAnswerSubmitted } from "../../../../utils/submissionUtils";

// A형 통합 진입 화면 — 지문 선택 + 섹션 선택 한 화면.
//
// [코드근거 — 무엇을 어디서 가져왔나]
// · 지문 정렬(완료 뒤로): ParagraphSelector.jsx의 sort 비교식 그대로.
// · 완료 지문 클릭 제한: ParagraphSelector.jsx의
//   `!isReview && !isPreview && paragraph.is_finished → not-allowed` 그대로.
// · 섹션 라벨: SectionSelector.jsx의
//   `type_name === "" ? showType(assignment.type, section.type) : type_name` 그대로.
// · 섹션 정렬(id 오름차순): SectionSelector.jsx의 sort 그대로.
// · vocabulary 특수 안내문: 두 셀렉터의 assignment?.type == "vocabulary" 분기 그대로.
// · 타입별 색: typeInfo[assignment.type]의 color/backgroundColor 토큰 그대로
//   (지문분석 #1DB05B/#E8F7EE · 내용이해 #6B4EE6/#EFEBFC · 어법 #E8920A/#FDF3E3
//    · 문장 #8A6D00/#FBF4D9 · 모의고사 #C2452D/#FCEDE9 …).
// · 화면 높이: 기존과 동일하게 getContentHeight(isMobile).
//
// [현행 대비 삭제된 것]
// · "다음" 버튼 → 지문 행 클릭이 onSelectParagraph(setParagraph+fetchSectionList) 수행.
// · "학습하기"(#438CFB 파란 사각) → 섹션 행 클릭이 onEnterSection(setSection+step3) 수행.
const FONT =
  "'Pretendard','Spoqa Han Sans Neo','Apple SD Gothic Neo','Noto Sans KR',sans-serif";

// 들어올 때 어느 지문을 펼칠지 고른다.
//
// 예전에는 **안 끝난 첫 지문**을 골랐다. 그런데 지문은 섹션을 전부 끝내야 완료가
// 되므로, 지문 셋을 하고 단어만 건너뛴 학생은 들어올 때마다 1번 지문으로 돌아갔다.
// 학생 눈에는 한 일이 사라진 것으로 보인다(2026-09-26 더슈프림 신고).
//
// 이제 **손을 댄 가장 마지막 지문**으로 보낸다. 거기까지 왔다는 뜻이기 때문이다.
// 그 지문마저 다 끝냈으면 그 뒤의 안 끝낸 지문으로 넘긴다. 아무 데도 손을 안 댔으면
// 예전처럼 첫 지문이다.
export const pickResumeParagraph = (sortedParagraphs, allowFinished) => {
  const selectable = (p) => allowFinished || !p.is_finished;
  const touched = (p) => (Number(p.finished_section_count) || 0) > 0;

  let lastTouchedIndex = -1;
  sortedParagraphs.forEach((p, index) => {
    if (touched(p) || p.is_finished) lastTouchedIndex = index;
  });

  if (lastTouchedIndex >= 0) {
    const last = sortedParagraphs[lastTouchedIndex];
    if (selectable(last)) return last;
    const next = sortedParagraphs.slice(lastTouchedIndex + 1).find(selectable);
    if (next) return next;
  }
  return sortedParagraphs.find(selectable);
};

const EntrySelector = ({
  entryReady = true, // [레이스 수정] 이번 과제의 지문 목록 fetch 완료 여부 (페이지가 전달)
  paragraphList = [],
  sectionList = [],
  selectedParagraph,
  onSelectParagraph,
  onEnterSection,
  isPreview = false,
  isReview = false,
}) => {
  const { assignment, sectionListStatus } = useSelector(
    (state) => state.assignment
  );
  const isMobile = useIsMobile();

  const accent = typeInfo[assignment?.type]?.color || "#00A396";
  // 수학 학습지는 지문이 아니라 학습지 하나에 30문제짜리 세트가 달린다(api/assignment/math_assignment.py)
  const isMath = String(assignment?.type || "").startsWith("math_");
  const paragraphWord = isMath ? "학습지" : "지문";
  const sectionWord = isMath ? "세트" : "섹션";
  const accentBg = typeInfo[assignment?.type]?.backgroundColor || "#F2FAF8";

  // 섹션 fetch 상태는 assignmentSlice의 sectionListStatus가 알려준다.
  // 섹션이 0개일 때 "요청이 실패한 것"과 "원래 없는 것"을 구분해야 하므로
  // 지문 클릭 시점의 로컬 플래그가 아니라 이 값을 그대로 쓴다.
  // idle 은 아직 부르지 않은 상태라 대기 표시로 묶는다 (아래 effect가 곧 부른다).
  const loadingSections =
    sectionListStatus === "loading" || sectionListStatus === "idle";
  const sectionFetchFailed = sectionListStatus === "failed";

  // 지문 정렬: 완료 지문 뒤로 (ParagraphSelector.jsx 비교식 그대로)
  const sortedParagraphs = paragraphList
    .slice()
    .sort((a, b) => {
      if (a.is_finished && !b.is_finished) return 1;
      if (!a.is_finished && b.is_finished) return -1;
      return 0;
    });

  // 자동 선택: 목록 도착 시 첫 선택 가능 지문을 골라 우측을 즉시 채운다.
  // (기존엔 사용자가 고르고 "다음"을 눌러야 fetchSectionList가 돌았음)
  const autoSelected = useRef(false);
  useEffect(() => {
    if (!entryReady) return; // [레이스 수정] 이전 과제의 낡은 목록으로 자동선택 금지
    if (autoSelected.current) return;
    if (selectedParagraph) {
      autoSelected.current = true;
      // 이전 화면에서 고른 지문이 남아 있는데 섹션은 비워진 경우 다시 불러온다.
      // 이게 없으면 idle 상태로 스켈레톤에 갇힌다.
      if (sectionListStatus === "idle") onSelectParagraph(selectedParagraph);
      return;
    }
    if (sortedParagraphs.length === 0) return;
    const firstSelectable = pickResumeParagraph(
      sortedParagraphs,
      isReview || isPreview
    );
    if (firstSelectable) {
      autoSelected.current = true;
      onSelectParagraph(firstSelectable);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paragraphList, entryReady]);

  const handleParagraphClick = (paragraph) => {
    // 완료 지문 클릭 제한 — ParagraphSelector.jsx 현행 로직 그대로
    if (!isReview && !isPreview && paragraph.is_finished) return;
    // [방어] 같은 지문 재클릭도 허용 — 섹션 불러오기 실패 시 재시도 경로가 된다.
    onSelectParagraph(paragraph);
  };

  // 섹션 정렬: id 오름차순 (SectionSelector.jsx 그대로)
  const sortedSections = sectionList.slice().sort((a, b) => a.id - b.id);

  // "이어하기" 배지: 미완료 섹션 중 첫 번째 (하나라도 완료가 있을 때만)
  const anyDone = sortedSections.some((s) => s.is_finished);
  const resumeId = anyDone
    ? sortedSections.find((s) => !s.is_finished)?.id
    : null;

  const sectionLabel = (section) =>
    section.type_name === ""
      ? showType(assignment?.type, section.type)
      : section.type_name;

  const paragraphState = (p) => {
    if (p.is_finished) return "done";
    if (selectedParagraph?.id === p.id) return "on";
    return "todo";
  };

  // 지문 진도 "5/6". 서버가 세어 내려준 값을 그대로 쓴다.
  const paragraphProgress = (p) => {
    const total = Number(p.section_count) || 0;
    const done = Number(p.finished_section_count) || 0;
    if (!total || p.is_finished) return null;
    if (!done) return null;
    return `${done}/${total}`;
  };

  return (
    <div
      style={{
        backgroundColor: "#FFF",
        height: getContentHeight(isMobile),
        display: "flex",
        flexDirection: "column",
        fontFamily: FONT,
      }}
    >
      {!entryReady && (
        <div style={{ padding: isMobile ? "14px" : "24px" }}>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                height: isMobile ? "46px" : "52px",
                borderRadius: "12px",
                backgroundColor: "#F4F8F7",
                marginBottom: "9px",
                maxWidth: "560px",
              }}
            />
          ))}
        </div>
      )}
      {entryReady && (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? "16px" : "24px",
          padding: isMobile ? "14px 4px 24px" : "24px 8px",
          // width: 100% 를 주면 안 된다. box-sizing 이 content-box 라 좌우 padding 이
          // 더해져 오른쪽으로 삐져나온다. 세로 flex 의 자식이라 안 줘도 폭을 채운다.
        }}
      >
        {/* ── 좌: 지문 목록 ── */}
        <div style={{ flex: isMobile ? "none" : 1.1, minWidth: 0 }}>
          <div
            style={{
              fontSize: isMobile ? "12px" : "13px",
              fontWeight: 800,
              color: "#7A8490",
              marginBottom: "10px",
            }}
          >
            {isMobile ? `① ${paragraphWord} 선택` : `${paragraphWord} ${paragraphList.length}개`}
            <span style={{ fontWeight: 600 }}>
              {" · 전체 완료 시 학습완료 처리 · 마감일까지 미완성 시 알림톡 발송"}
            </span>
          </div>
          {assignment?.type === "vocabulary" && (
            <div
              style={{
                fontSize: isMobile ? "11px" : "12px",
                fontWeight: 600,
                color: "#7A8490",
                marginBottom: "10px",
              }}
            >
              단어장, 매칭게임은 지속적인 학습을 위해, 진행해도 완료 처리되지
              않습니다.
            </div>
          )}
          {sortedParagraphs.length === 0 && (
            <div
              style={{
                border: "1px dashed #B9C1C9",
                borderRadius: "12px",
                padding: "22px 16px",
                textAlign: "center",
                color: "#7A8490",
                fontSize: isMobile ? "12px" : "13px",
                fontWeight: 600,
              }}
            >
              표시할 지문이 없습니다.
            </div>
          )}
          {sortedParagraphs.map((paragraph, index) => {
            const state = paragraphState(paragraph);
            const disabled =
              !isReview && !isPreview && paragraph.is_finished;
            return (
              <div
                key={paragraph.id}
                onClick={() => handleParagraphClick(paragraph)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "11px",
                  padding: isMobile ? "12px 12px" : "14px 16px",
                  marginBottom: "8px",
                  borderRadius: "12px",
                  border:
                    state === "on"
                      ? `1.5px solid ${accent}`
                      : "1px solid #E6EDEC",
                  backgroundColor:
                    state === "on"
                      ? accentBg
                      : state === "done"
                      ? "#F4F8F7"
                      : "#FFFFFF",
                  color: state === "done" ? "#7A8490" : "#3A434E",
                  fontWeight: 700,
                  fontSize: isMobile ? "13px" : "15px",
                  cursor: disabled ? "not-allowed" : "pointer",
                  transition: "border-color .12s, background-color .12s",
                }}
              >
                <span
                  style={{
                    width: isMobile ? "23px" : "26px",
                    height: isMobile ? "23px" : "26px",
                    borderRadius: "8px",
                    backgroundColor: state === "done" ? "#EDF2F1" : accentBg,
                    color: state === "done" ? "#7A8490" : accent,
                    fontWeight: 800,
                    fontSize: isMobile ? "11px" : "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "none",
                  }}
                >
                  {index + 1}
                </span>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {paragraph.title}
                </span>
                {/* [전체 진도 v1] 이 지문의 모든 섹션 합산 — 선택된 지문만 계산 가능
                    (섹션 목록은 선택 시점에 로드되므로) */}
                {state === "on" && sortedSections.length > 0 && (() => {
                  let total = 0;
                  let submitted = 0;
                  sortedSections.forEach((sec) => {
                    const t = typeof sec.total_count === "number"
                      ? sec.total_count
                      : (Array.isArray(sec.answer) ? sec.answer.length : 0);
                    const d = typeof sec.submitted_count === "number"
                      ? sec.submitted_count
                      : (Array.isArray(sec.answer)
                          ? sec.answer.filter(isAnswerSubmitted).length
                          : 0);
                    total += t;
                    submitted += sec.is_finished ? t : d;
                  });
                  if (total === 0) return null;
                  const pct = Math.round((submitted / total) * 100);
                  return (
                    <div style={{ display: "flex", alignItems: "center", gap: "7px", flex: "none" }}>
                      <div style={{
                        width: isMobile ? "60px" : "90px",
                        height: "5px",
                        borderRadius: "3px",
                        backgroundColor: "#E6EDEC",
                        overflow: "hidden",
                      }}>
                        <div style={{
                          width: `${pct}%`,
                          height: "100%",
                          borderRadius: "3px",
                          backgroundColor: accent,
                          transition: "width .3s",
                        }} />
                      </div>
                      <span style={{
                        fontSize: isMobile ? "10px" : "11px",
                        fontWeight: 800,
                        color: accent,
                        minWidth: "30px",
                      }}>
                        {pct}%
                      </span>
                    </div>
                  );
                })()}
                <span
                  style={{
                    borderRadius: "999px",
                    fontSize: isMobile ? "10px" : "11px",
                    fontWeight: 800,
                    padding: "2px 9px",
                    flex: "none",
                    backgroundColor:
                      state === "on"
                        ? accentBg
                        : state === "done"
                        ? "#EDF2F1"
                        : "#F2F5F4",
                    color:
                      state === "on"
                        ? accent
                        : paragraphProgress(paragraph)
                        ? accent
                        : "#7A8490",
                  }}
                >
                  {/* 손댄 지문을 '시작 전' 으로 보여주면 안 된다. 여섯 중 다섯을
                      끝낸 지문과 손도 안 댄 지문이 똑같아 보여서, 학생이 한 일이
                      사라진 줄 안다(2026-09-26 더슈프림 신고). 몇 개 했는지 적는다. */}
                  {state === "done"
                    ? "완료"
                    : paragraphProgress(paragraph) ||
                      (state === "on" ? "학습중" : "시작 전")}
                </span>
              </div>
            );
          })}
        </div>

        {/* ── 우: 선택 지문의 섹션 목록 ── */}
        <div style={{ flex: isMobile ? "none" : 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: isMobile ? "12px" : "13px",
              fontWeight: 800,
              color: "#7A8490",
              marginBottom: "10px",
            }}
          >
            {isMobile
              ? `② ${sectionWord}, 누르면 바로 시작`
              : `선택한 ${paragraphWord}의 ${sectionWord}, 누르면 바로 시작`}
          </div>

          {!selectedParagraph && (
            <div
              style={{
                border: "1px dashed #B9C1C9",
                borderRadius: "12px",
                padding: "26px 16px",
                textAlign: "center",
                color: "#7A8490",
                fontSize: isMobile ? "12px" : "13px",
                fontWeight: 600,
              }}
            >
              먼저 {isMath ? "학습지를" : "지문을"} 선택해주세요.
            </div>
          )}

          {selectedParagraph && loadingSections && (
            <div>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    height: isMobile ? "44px" : "48px",
                    borderRadius: "10px",
                    backgroundColor: "#F4F8F7",
                    marginBottom: "8px",
                  }}
                />
              ))}
            </div>
          )}

          {selectedParagraph && !loadingSections && sortedSections.length === 0 && (
            <div
              style={{
                border: "1px dashed #B9C1C9",
                borderRadius: "12px",
                padding: "22px 16px",
                textAlign: "center",
                color: "#7A8490",
                fontSize: isMobile ? "12px" : "13px",
                fontWeight: 600,
                lineHeight: 1.7,
              }}
            >
              {sectionFetchFailed ? (
                <>
                  섹션을 불러오지 못했어요.
                  <br />
                  지문을 다시 한 번 눌러 재시도해주세요.
                </>
              ) : (
                <>
                  이 지문에는 학습할 내용이 없어요.
                  <br />
                  다시 눌러도 나타나지 않으니 선생님께 문의해주세요.
                </>
              )}
            </div>
          )}

          {selectedParagraph &&
            !loadingSections &&
            sortedSections.map((section, index) => {
              const done = section.is_finished;
              return (
                <div
                  key={section.id}
                  onClick={() => onEnterSection(section)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: isMobile ? "12px 12px" : "13px 14px",
                    marginBottom: "8px",
                    borderRadius: "10px",
                    border: "1px solid #E6EDEC",
                    backgroundColor: "#FFFFFF",
                    color: done ? accent : "#3A434E",
                    fontWeight: 600,
                    fontSize: isMobile ? "13px" : "14px",
                    cursor: "pointer",
                    transition: "border-color .12s, background-color .12s",
                  }}
                >
                  <span
                    style={{
                      width: "17px",
                      height: "17px",
                      borderRadius: "50%",
                      border: `1.5px solid ${done ? accent : "#B9C1C9"}`,
                      backgroundColor: done ? accent : "transparent",
                      color: done ? "#FFFFFF" : "#7A8490",
                      fontSize: "9px",
                      fontWeight: 800,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: "none",
                    }}
                  >
                    {done ? "✓" : index + 1}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {sectionLabel(section)}
                  </span>
                  {resumeId === section.id && (
                    <span
                      style={{
                        backgroundColor: "#0E141B",
                        color: "#FFFFFF",
                        borderRadius: "999px",
                        fontSize: isMobile ? "9px" : "10px",
                        fontWeight: 800,
                        padding: "2px 8px",
                        flex: "none",
                      }}
                    >
                      이어하기
                    </span>
                  )}
                  {/* [진도 바 v1] 섹션별 진행률 — answer 배열 기준 */}
                  {(() => {
                    // [진도 v3] 서버 계산 필드(submitted_count/total_count) 우선,
                    // 없으면 answer 배열로 폴백 — 어느 쪽이든 진도가 뜨도록
                    const total = typeof section.total_count === "number"
                      ? section.total_count
                      : (Array.isArray(section.answer) ? section.answer.length : 0);
                    const submitted = typeof section.submitted_count === "number"
                      ? section.submitted_count
                      : (Array.isArray(section.answer)
                          ? section.answer.filter(isAnswerSubmitted).length
                          : 0);
                    if (done || total === 0) return null;
                    const pct = Math.round((submitted / total) * 100);
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", flex: "none" }}>
                        <div style={{
                          width: isMobile ? "36px" : "48px",
                          height: "4px",
                          borderRadius: "2px",
                          backgroundColor: "#E6EDEC",
                          overflow: "hidden",
                        }}>
                          <div style={{
                            width: `${pct}%`,
                            height: "100%",
                            borderRadius: "2px",
                            backgroundColor: accent,
                            transition: "width .3s",
                          }} />
                        </div>
                        <span style={{
                          fontSize: isMobile ? "10px" : "11px",
                          fontWeight: 800,
                          color: accent,
                        }}>
                          {submitted}/{total}
                        </span>
                      </div>
                    );
                  })()}
                  <span
                    style={{
                      color: "#B9C1C9",
                      fontWeight: 800,
                      flex: "none",
                    }}
                  >
                    ›
                  </span>
                </div>
              );
            })}
          {/* 수학: 세트 목록 아래 '빠른 정답 입력'(종이에 푼 답만 넣기). 세트를 다 채점하면 같은 자리가 '틀린 문제' */}
          {isMath && selectedParagraph && !loadingSections && sortedSections.length > 0 && (() => {
            const isAllDone = sortedSections.every((one) => one.is_finished);
            return (
              <div
                key="math-quick"
                onClick={() =>
                  onEnterSection({
                    id: `quick-${selectedParagraph.id}`,
                    quickParagraphId: selectedParagraph.id,
                    type: "math",
                    type_name: isAllDone ? "틀린 문제" : "빠른 정답 입력",
                  })
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: isMobile ? "12px 12px" : "13px 14px",
                  marginTop: "4px",
                  marginBottom: "8px",
                  borderRadius: "10px",
                  border: `1.5px dashed ${isAllDone ? "#FF9DB1" : accent}`,
                  backgroundColor: isAllDone ? "#FFF8FA" : accentBg,
                  color: isAllDone ? "#D63C5E" : accent,
                  fontWeight: 800,
                  fontSize: isMobile ? "13px" : "14px",
                  cursor: "pointer",
                }}
              >
                <span style={{ flex: 1 }}>{isAllDone ? "틀린 문제" : "빠른 정답 입력"}</span>
                <span style={{ fontSize: "16px" }}>›</span>
              </div>
            );
          })()}
        </div>
      </div>
      )}
    </div>
  );
};

export default EntrySelector;
